import React, { type ReactNode, useContext, useMemo, useState } from 'react';

import { Filter } from '@pkg/classes';

import { DEFAULT_FILTER_DATA, FilterContext } from './context';
import { type FilterContextDescriptor, type FilterData, type FilterDispatch } from './types';

export interface FilterProviderProps {
    children: ReactNode;
}

export function FilterProvider({ children }: FilterProviderProps) {
    const [filters, updateFilters] = useState<FilterData>(DEFAULT_FILTER_DATA);

    const context: FilterContextDescriptor = useMemo(() => {
        // The `filters` that gets returned by `context` is a "detached"
        // clone of what is stored in `state`. This is what protects our
        // `state` from changing outside of a render cycle.
        const clonedFilters: FilterData = new Filter(filters);

        const setFilters: FilterDispatch = (state) => {
            if (typeof state === 'function') {
                updateFilters(state);
            } else {
                updateFilters((prevFilters) => {
                    // Manual equality check is necessary since "object reference"
                    // will be different between render cycles.
                    const identical = prevFilters.serialize === state.serialize;
                    return identical ? prevFilters : state;
                });
            }
        };

        return [clonedFilters, setFilters];
    }, [filters, updateFilters]);

    return <FilterContext.Provider value={context}>{children}</FilterContext.Provider>;
}

export function useFilter() {
    return useContext(FilterContext);
}
